import type { Messages } from '../messages.js'

/**
 * Brazilian Portuguese. Not a copy of the European locale with the flag swapped: `rodada` for
 * a round, `embaralhado` for a shuffle, the gerund where Portugal uses `a` plus the infinitive,
 * and the pronoun before the verb rather than hyphenated after it.
 */
export const ptBR: Messages = {
  tag: 'pt-BR',

  readingDictionary: 'Lendo o dicionário…',
  noWordList: 'Sem lista de palavras para "{language}". Gere uma:  pnpm dictionary build',
  emptyWordList: 'A lista de palavras para "{language}" está vazia.',

  flips: 'viradas',
  score: 'pontos',
  words: 'palavras',
  round: 'rodada',
  ticksLeftLabel: 'Tempo restante nesta rodada',
  typeAWord: 'digite uma palavra',
  tapPrompt: 'toque para pegar ou devolver, depois {action}',

  boardOfTiles: 'Tabuleiro de {n} peças',
  faceDown: 'virada para baixo',
  wildCard: 'curinga',
  wildKey: 'qualquer letra',
  letterReplaced: '{from} virou {to}',
  letterSwap: 'TROCA DE LETRA!',
  spentTile: 'peça gasta',
  hiddenWhilePaused: 'oculta na pausa',
  letterInWord: '{letter}, letra {position} da palavra',

  completeWord: 'Completar palavra',
  completeShort: 'Completar',
  reset: 'Limpar',
  pause: 'Pausar',
  resume: 'Continuar',
  newGame: 'Novo jogo',
  paused: 'Pausado',
  outOfFlips: 'Sem viradas',
  finalResult: '{score} pontos com {words} em {rounds}',
  playAgain: 'Jogar novamente',
  share: 'Compartilhar',
  shareCopied: 'Copiado.',
  shareSelect: 'Copie isto:',

  lettersSelect: 'as letras selecionam',
  keysWild: 'usado quando você digita uma letra que nenhuma peça mostra',
  clearsEvery: 'remove todos os {letter} selecionados',
  undoLastLetter: 'desfaz a última letra',
  noWordsYet: 'Nenhuma palavra ainda.',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: 'já encontrada',
  reasonTooShort: 'muito curta',
  reasonNotAWord: 'não é uma palavra',
  reasonAllFound: 'você já tem todos',
  noSuchLetterUp: 'nenhum {letter} à vista',
  nothingUp: 'nada à vista',
  letterHidden: 'uma letra se escondeu',
  shuffled: 'embaralhado',
  shuffledAndBilled: 'embaralhado, cobradas {flips} viradas não usadas',
  tooFewLetters: 'Restam poucas letras',

  gameLanguage: 'idioma',
  interfaceLanguage: 'interface',
  dictionarySize: '{common} comuns de {full} palavras',
  filterLanguages: 'Buscar idiomas',
  noMatches: 'Nenhum resultado',

  nerdMode: 'modo técnico',
  rules: 'Regras',
  difficulty: 'dificuldade',
  difficultyNames: { easy: 'fácil', medium: 'médio', hard: 'difícil', insane: 'brutal' },
  pausedNoBoard: 'Esta partida não vai aparecer no ranking.',
  about: 'Sobre',
  themeLabel: 'tema',
  themeNames: { traditional: 'tradicional', light: 'claro', contrast: 'alto contraste' },
  tiles: 'peças (N)',
  secondsPerTick: 'segundos / tempo',
  holdTicks: 'tempos de espera',
  minWord: 'palavra mínima',
  startingFlips: 'viradas iniciais',
  wildChance: 'chance de curinga',
  replaceChance: 'chance de troca de letra',
  hideChance: 'chance de ocultar letra',
  wordCompleteMode: 'palavra completa',
  wordCompleteNames: { shuffle: 'embaralhar', spend: 'gastar', keep: 'manter' },
  flipEconomy: 'economia de viradas',
  flipEconomyNames: {
    none: 'nenhuma',
    perLetter: 'por letra',
    fibonacci: 'fibonacci',
    overMinimum: 'acima do mínimo',
  },
  repeatedLetterKey: 'tecla de letra repetida',
  keySchemeNames: { cycle: 'ciclar', advance: 'avançar' },
  keySchemeHelp: {
    cycle:
      'A pega o próximo A livre e, quando todos estão na palavra, os remove. ' +
      'Shift+A também os remove.',
    advance: 'A pega o próximo A livre. Shift+A remove todos os A da palavra.',
  },

  whatThatMeans: 'O que isso significa',
  factRound: 'uma rodada',
  factWholeBoardUp: 'tabuleiro todo à vista',
  factRoundCosts: 'uma rodada custa',
  factFlipsBuy: 'as viradas iniciais dão',
  factThisBoard: 'este tabuleiro',
  factBoardHadToAdmit: 'o tabuleiro tinha que admitir',
  ticksAndSeconds: '{ticks}, {seconds}',
  wordsLongest: '{words}, a mais longa {longest}',
  wordsIncludingOneOf: '{words} incluindo uma de {ceiling}',
  scorelessRounds: '{rounds} sem pontuar',

  whatAWordPays: 'O que uma palavra rende',
  columnLetters: 'letras',
  columnCost: 'custo',
  columnPoints: 'pontos',
  columnFlips: 'viradas',
  columnNet: 'líquido',

  canonicalRules: 'Regras {difficulty} oficiais.',
  customRules: 'Alterado do padrão. Pontuações com regras próprias não são classificadas.',
  applyAndStart: 'Aplicar e começar um jogo',
  changesNextGame: 'As alterações valem a partir do próximo jogo.',
  presets: 'Padrões:',

  start: 'Começar',
  restart: 'Reiniciar',
  quit: 'Sair',
  quitTitle: 'Sair deste jogo?',
  restartTitle: 'Reiniciar este jogo?',
  restartConfirm: 'Reiniciar',
  quitConfirm: 'Sair',
  keepPlaying: 'Continuar jogando',
  personalBest: 'Seus melhores jogos',
  newPersonalBest: 'Novo recorde pessoal.',
  columnRank: '#',
  notRanked: 'Regras próprias: este jogo não é classificado.',
  notRankedPaused: 'O relógio parou: este jogo não é classificado.',
  rankOfTotal: '{rank} de {total}',

  howToPlay: 'Como jogar',

  backToGame: 'Voltar ao jogo',
  welcomeTitle: 'Boas-vindas ao Blinkered',
  tutorialSkip: 'Pular',
  tutorialNext: 'Avançar',
  tutorialBack: 'Voltar',
  tutorialStart: 'Começar a jogar',
  tutorialHideAgain: 'Não mostrar de novo',
  tutorialProgress: '{n} de {total}',
  tutorialSkipTitle: 'Pular a apresentação?',
  tutGoalTitle: 'Blinkered é uma corrida',
  tutGoalBody:
    'Você começa o jogo com um número fixo de viradas, e cada peça que vira custa uma virada. Quando as viradas acabam, o jogo termina. Formar palavras rende viradas, e quanto mais longa a palavra, mais viradas você ganha.',
  tutSpentBody:
    'As letras que você usa em uma palavra ficam viradas para baixo até a próxima rodada. Só uma troca de letra muda quais letras estão no tabuleiro.',
  tutChangeTitle: 'As letras podem mudar',
  tutSameLetters:
    'Viu uma palavra que não deu tempo de jogar? As mesmas letras voltam na próxima rodada, embaralhadas em novos lugares.',
  tutPickLetters: 'Toque nas letras que quiser, em ordem, para formar uma palavra.',
  tutMoreTurn:
    'As peças continuam virando enquanto você pensa, então ainda pode vir uma letra melhor.',
  tutTapBack:
    'Tocou em uma sem querer? Toque de novo para devolver. Em qualquer uma, não só na última.',
  tutComplete: 'Aperte Concluir quando a palavra estiver pronta.',
  htBoardTitle: 'O tabuleiro',
  htBoardBody:
    'As peças viram uma a uma, em uma ordem aleatória. As peças à vista servem para formar palavras.',
  htWordsTitle: 'As palavras',
  htWordsBody: 'Monte uma palavra com as peças à vista, digitando ou clicando nas letras em ordem.',
  htFlipsTitle: 'As viradas',
  htFlipsBody:
    'Cada peça que vira custa uma virada. Uma palavra completa devolve viradas ao seu total, e palavras longas devolvem mais. Quando as viradas acabam, o jogo termina.',
  htRoundTitle: 'A rodada',
  htRoundBody:
    'Quando vira a última peça de uma rodada, o tabuleiro fica todo à vista por um instante. Depois as peças são viradas para baixo e embaralhadas, e começa uma rodada nova.',
  htLanguagesTitle: 'Os idiomas',
  htLanguagesBody:
    '{n}. Cada tabuleiro pode ser resolvido com palavras de uso corrente. Uma palavra incomum também pontua, se o dicionário a conhecer.',
  htKeysTitle: 'O teclado',
  htWildTitle: 'Os curingas',
  htWildBody:
    'Às vezes aparece um curinga no lugar de uma letra. Um curinga vale qualquer letra que forme uma palavra válida. Uma palavra já completada não conta.',
  htHideTitle: 'Letras que se escondem',
  htHideBody:
    'Nos níveis mais difíceis uma letra visível pode se virar sozinha e voltar mais tarde na rodada. O cronômetro ganha um traço por isso, em vermelho. As letras que você já pegou não se viram.',
  tutHideBody:
    'Nos níveis mais difíceis uma letra visível pode se virar sozinha e voltar mais tarde na rodada. As letras que você já pegou não se viram.',
  htSwapTitle: 'As letras mudam',
  htSwapBody:
    'Às vezes, entre rodadas, uma letra é substituída por outra. Você vai ver qual letra saiu e qual entrou.',
  htLevelsTitle: 'Os níveis',
  htLevelEasy:
    'As mesmas doze letras durante o jogo inteiro: dá para aprendê-las e guardar uma lista de palavras na cabeça. As peças viram devagar e o tabuleiro completo fica à vista tempo suficiente para terminar de escolher as letras.',
  htLevelMedium:
    'De vez em quando uma letra muda, então fica mais difícil guardar as palavras que você queria jogar mais adiante. Menos tempo para olhar e menos para pensar.',
  htLevelHard:
    'Palavras de três letras deixam de contar e uma letra muda mais ou menos a cada duas rodadas. O tabuleiro mal aparece e já embaralha.',
  htLevelInsane:
    'Tudo ao mesmo tempo, na velocidade máxima. O tabuleiro é embaralhado quase logo depois da última virada.',
  htTouchTitle: 'A tela sensível ao toque',
  htTouchBody:
    'Toque numa peça virada para pegar a letra dela. Toque numa letra já pega para devolvê-la. Completar e Limpar ficam abaixo do tabuleiro.',

  accountTitle: 'Sua conta',
  menuProfile: 'Meu perfil',
  menuGames: 'Meus jogos',
  menuPublicPage: 'Minha página pública',
  menuSignedInAs: 'Conectado como',
  signIn: 'Entrar',
  signOut: 'Sair',
  signInTitle: 'Entre ou crie uma conta',
  signInLead: 'Uma conta guarda seus jogos, em todos os aparelhos.',
  signInKeepGame: 'Entre para guardar este jogo, e todos os seguintes.',
  continueWithApple: 'Continuar com a Apple',
  continueWithGoogle: 'Continuar com o Google',
  signInOr: 'ou',
  emailLabel: 'E-mail',
  codeLabel: 'Código',
  emailMeACode: 'Me envie um código',
  working: 'Um instante…',
  sendNewCode: 'Enviar um novo código',
  codeSent: 'Confira seu e-mail. O código vale por 10 minutos.',
  notNow: 'Agora não',
  badEmail: 'Isso não parece um endereço de e-mail.',
  badCode: 'Esse código não funcionou. Os códigos expiram em 10 minutos e valem uma vez só.',
  serverBusy: 'Não foi possível falar com o servidor. Tente daqui a pouco.',
  serverDown: 'Não foi possível falar com o servidor.',
  offline: 'Sem conexão',
  leaderboardTitle: 'Classificação',
  leaderboardEmpty: 'Ainda não há partidas nesta classificação. Seja o primeiro.',
  leaderboardWouldBe: 'Onde esta partida ficaria',
  leaderboardThisGame: 'Esta partida',
  leaderboardRanksHere: 'Onde esta partida fica',
  leaderboardOutside: 'Fora do top 5.',
  tutAccountTitle: 'Guarde suas pontuações',
  tutAccountBody:
    'Uma conta salva suas partidas, coloca suas pontuações na classificação e mantém seu perfil.',
  ssoCancelled: 'Entrada cancelada.',
  ssoExpired: 'Demorou demais. Tente entrar de novo.',
  ssoNoUsername: 'Não deu para terminar de criar a conta. Tente daqui a pouco.',
  ssoFailed: 'Não deu certo. Tente de novo, ou use seu e-mail.',
  tabProfile: 'Perfil',
  countryAny: 'Prefiro não dizer',
  findCountry: 'Buscar países',
  usernameLabel: 'Nome de usuário',
  bioLabel: 'Bio',
  countryLabel: 'País',
  bioHint: 'Sem links. {left}/{max}',
  save: 'Salvar',
  saving: 'Salvando…',
  saved: 'Salvo.',
  nameTaken: 'Esse nome já é de alguém.',
  nameTooShort: 'Um pouco mais longo, por favor.',
  nameTooLong: 'É longo demais.',
  nameBadCharacters: 'Só letras, números, hifens e sublinhados.',
  nameBadEdges: 'Precisa começar e terminar com letra ou número.',
  nameMixedScripts: 'Escolha um alfabeto só e fique nele.',
  nameReserved: 'Esse parece um nome que nós mesmos damos. Escolha outro.',
  nameUnusable: 'Esse nome não pode ser usado.',
  bioTooLong: 'As bios têm no máximo {max} caracteres.',
  bioHasLink: 'Links não são permitidos na bio.',
  bioHasControl: 'Contém caracteres que uma bio não aceita.',
  bioUnusable: 'Essa bio não pode ser usada.',
  gamesHeading: 'Jogos',
  gamesEmpty: 'Ainda não tem nada aqui. Os jogos que você fizer conectado aparecem aqui.',
  gamesEmptyShort: 'Ainda não tem nada aqui.',
  gamesLoading: 'Lendo seus jogos…',
  theirGamesLoading: 'Lendo os jogos dele…',
  profileLoading: 'Lendo o perfil…',
  playerNotFound: 'Não tem ninguém aqui com esse nome.',
  columnWhen: 'Quando',
  columnRounds: 'rodadas',
  columnGame: 'Jogo',
  gameLoading: 'Lendo o jogo…',
  gameUnreadable: 'Não foi possível ler este jogo.',
  gameNotRanked: 'Jogado com regras alteradas, então não entra no ranking.',
  detailNotKept: 'As palavras e os tabuleiros deste jogo não são mais guardados.',
  openingBoard: 'O tabuleiro inicial',
  roundByRound: 'Rodada a rodada',
  roundNothing: 'nada encontrado',
  changeReplaced: '{letter} trocada na casa {slot}',
  changeWildOn: '{letter} coringa na casa {slot}',
  changeWildOff: '{letter} coringa tirado da casa {slot}',
  keepThisGame: 'Guardar este jogo',
  savedToYourGames: 'Salvo nos seus jogos.',

  reportThis: 'Denunciar',
  reportHeading: 'Denunciar isto',
  reportTheName: 'O nome',
  reportTheBio: 'A descrição',
  reportTheScore: 'A pontuação',
  reportWhy: 'O que há de errado? Opcional.',
  reportSend: 'Enviar denúncia',
  reportSent: 'Obrigado. Alguém vai olhar.',
  reportSignIn: 'Entre para denunciar algo.',
  reportGone: 'Isso não está mais aí.',
  reportCancel: 'Cancelar',

  deleteAccount: 'Excluir minha conta',
  deleteAccountWhat:
    'Isso remove seu perfil e todos os jogos que você guardou, para sempre. Não pode ser desfeito.',
  deleteAccountAsk: 'Me envie um código',
  deleteConfirm: 'Excluir para sempre',
  deleteGone: 'Sua conta não existe mais. Obrigado por jogar.',
  deleteNoAddress:
    'Não há um endereço confirmado nesta conta, então uma exclusão não pode ser verificada. Fale com a gente e faremos isso manualmente.',

  rulesetFacts: 'Palavras de {letters} · uma peça nova a cada {seconds}',
  plurals: {
    words: { one: '{n} palavra', other: '{n} palavras' },
    rounds: { one: '{n} rodada', other: '{n} rodadas' },
    flips: { one: '{n} virada', other: '{n} viradas' },
    ticks: { one: '{n} tempo', other: '{n} tempos' },
    points: { one: '{n} ponto', other: '{n} pontos' },
    minLetters: { one: '{n} letra ou mais', other: '{n} letras ou mais' },
    seconds: { one: '{n} segundo', other: '{n} segundos' },
  },
}
